import { Section, Container, Eyebrow, Reveal } from "../components/site/primitives";
import { StatBand, BenefitGrid, ComparisonBlock, CTABanner } from "../components/site/blocks";
import Seo from "../components/site/Seo";

const breakItems = [
  { icon: "TrendingDown", label: "You lose the transaction", body: "A loan gets denied in underwriting, a return gets filed with the debt behind it still unresolved, or a financial plan hits a wall it can't get past." },
  { icon: "UserMinus", label: "You lose the customer", body: "They leave to find help elsewhere, and the acquisition cost goes with them." },
  { icon: "CircleDollarSign", label: "You lose the future revenue", body: "One blocked moment ends the relationship and the lifetime value behind it." },
];

const breakpoints = [
  { icon: "Home", label: "Mortgage loans & refinances", body: "Stall or fail late in underwriting." },
  { icon: "Banknote", label: "Personal & small-business loans", body: "Outstanding IRS tax debt can delay underwriting, reduce options, or prevent funding." },
  { icon: "FileText", label: "Tax preparation", body: "A balance due or unfiled prior years leaves the journey incomplete with no path forward." },
  { icon: "Briefcase", label: "Financial & legal plans", body: "IRS tax debt and enforcement actions disrupt planning, legal strategies, and outcomes." },
];

const impact = [
  { icon: "CheckCircle2", label: "Complete more outcomes", body: "Finish more revenue-critical transactions and filings." },
  { icon: "Repeat", label: "Reduce churn & drop-off", body: "Keep customers engaged through the moments that matter." },
  { icon: "ArrowUpRight", label: "Increase lifetime value", body: "Extend the relationship far beyond a single transaction." },
  { icon: "ShieldCheck", label: "Strengthen your position", body: "Become a complete financial solution for your customers." },
];

const whyUs = [
  { icon: "PercentCircle", label: "Revenue Share Opportunities", body: "A predictable, incremental revenue line built into your existing customer base." },
  { icon: "Settings2", label: "Fully managed execution", body: "We handle all IRS communication, representation, and resolution. No operational lift for your team." },
  { icon: "GraduationCap", label: "Decades of Expertise", body: "Attorneys, CPAs, and Enrolled Agents resolving cases at scale." },
  { icon: "Handshake", label: "Relationship-preserving model", body: "You retain the customer, brand, and trust. We operate behind the scenes." },
  { icon: "ShieldCheck", label: "Enterprise compliance & consistency", body: "Standardized processes designed for regulated, high-volume environments." },
  { icon: "BarChart3", label: "Transparent reporting", body: "Lifecycle visibility into every case, with clear expectations throughout." },
];

const econItems = [
  "A meaningful share of your customers carry unresolved tax debt",
  "Once identified and supported, 40–50% engage in resolution",
  "Resolution services unlock incremental, high-value revenue",
  "Monitoring and compliance services extend lifetime value",
];

export default function WhyPartner() {
  return (
    <>
      <Seo
        title="Why Partner With Community Tax | Enterprise Tax Debt Resolution"
        description="See the market opportunity, partner benefits, and economics behind embedding tax debt resolution into your platform."
        path="/why-partner"
      />
      {/* The Opportunity */}
      <section id="the-opportunity" className="pt-16 pb-10 lg:pt-24">
        <Container>
          <Eyebrow>Market Reality</Eyebrow>
          <Reveal>
            <h1 className="font-display font-extrabold text-[#1E1E24] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
              Tax Debt is Disrupting Your Business Right Now
            </h1>
          </Reveal>
          <div className="mt-12 bg-white rounded-[28px] ct-shadow-card p-8 lg:p-12">
            <StatBand
              stats={[
                { value: "18.6M", caption: "Individual taxpayers with overdue taxes" },
                { value: "$316B", caption: "Owed to the IRS (year-end 2022)" },
              ]}
            />
          </div>
          <Reveal delay={0.1}>
            <p className="mt-8 text-lg text-[#40424D] leading-relaxed max-w-3xl">
              These aren't just IRS statistics — they're your clients, blocking transactions today.
              Tax debt is widespread, under-detected, and rarely visible in traditional financial
              data models. By the time it surfaces, customer journeys are already broken.
            </p>
          </Reveal>
        </Container>
      </section>

      <Section className="bg-white">
        <Eyebrow>What Breaks Without a Solution</Eyebrow>
        <Reveal>
          <h2 className="font-display font-extrabold text-[#1E1E24] text-3xl md:text-4xl leading-tight max-w-3xl mb-12">
            Tax debt is a workflow disruption across every financial, tax, and legal ecosystem.
          </h2>
        </Reveal>
        <BenefitGrid items={breakItems} />
      </Section>

      <Section>
        <Eyebrow>Where Tax Debt Breaks Outcomes</Eyebrow>
        <Reveal>
          <p className="text-lg text-[#40424D] leading-relaxed max-w-3xl mb-12">
            Unresolved tax debt usually surfaces late — after time, effort, and trust have already
            been invested. The most common breakpoints:
          </p>
        </Reveal>
        <BenefitGrid items={breakpoints} cols={2} />
        <Reveal delay={0.1}>
          <div className="mt-16 max-w-3xl">
            <h3 className="font-display text-2xl font-bold text-[#1E1E24] mb-4">
              Why the market fails today
            </h3>
            <p className="text-[#40424D] leading-relaxed">
              Tax resolution sits outside core financial workflows. Customers get sent away instead
              of guided forward — forcing you to choose between losing the transaction and losing the
              relationship.
            </p>
          </div>
        </Reveal>
      </Section>

      <Section className="bg-white">
        <Eyebrow>The Impact of Solving It</Eyebrow>
        <Reveal>
          <h2 className="font-display font-extrabold text-[#1E1E24] text-3xl md:text-4xl leading-tight max-w-3xl mb-12">
            Partners who embed tax debt resolution win on every metric.
          </h2>
        </Reveal>
        <BenefitGrid items={impact} cols={2} />
        <div className="mt-16">
          <StatBand
            stats={[
              { value: "~80%", caption: "Investigated clients proceed to resolution" },
              { value: "2–7 days", caption: "To complete initial investigation" },
              { value: "EN · ES", caption: "Full bilingual servicing" },
              { value: "48 states", caption: "Coverage across the U.S." },
            ]}
          />
          <Reveal delay={0.1}>
            <p className="mt-10 text-[#1E1E24] font-medium text-lg leading-relaxed max-w-3xl">
              Embed resolution directly into these moments and you prevent fallout before customers
              disengage or seek help elsewhere.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* Why Partner With Us */}
      <Section id="why-us">
        <Eyebrow>Why Partner With Us</Eyebrow>
        <Reveal>
          <h2 className="font-display font-extrabold text-[#1E1E24] text-3xl md:text-4xl lg:text-5xl leading-tight max-w-3xl">
            Built to Resolve at Scale — Invisibly
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-lg text-[#40424D] leading-relaxed max-w-3xl mb-14">
            Founded by attorneys, Community Tax brings enterprise discipline to tax debt
            representation — combining expertise, transparent pricing, and operational scale.
          </p>
        </Reveal>
        <BenefitGrid items={whyUs} />
        <Reveal delay={0.1}>
          <div className="mt-16 bg-white rounded-[28px] ct-shadow-card p-8 lg:p-10">
            <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#0262B4] mb-3">
              Built for Scale
            </p>
            <p className="text-[#1E1E24] font-medium text-lg">
              Nationwide coverage · English & Spanish servicing · A predictable revenue line of
              business · Designed for volume
            </p>
          </div>
        </Reveal>
        <div className="mt-12 ct-grad-navy rounded-[28px] p-8 lg:p-12">
          <StatBand
            label="At a Glance"
            dark
            stats={[
              { value: "130,000+", caption: "Clients served" },
              { value: "$2.3B+", caption: "Tax debt resolved" },
              { value: "15+ yrs", caption: "Representation expertise" },
            ]}
          />
        </div>
      </Section>

      {/* The Economics */}
      <Section id="the-economics" className="bg-white">
        <Eyebrow>The Economics</Eyebrow>
        <Reveal>
          <h2 className="font-display font-extrabold text-[#1E1E24] text-3xl md:text-4xl lg:text-5xl leading-tight max-w-3xl">
            A Revenue Opportunity Hidden in Plain Sight
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-6 text-lg text-[#40424D] mb-10 max-w-3xl">
            Across enterprise partners, the same pattern holds:
          </p>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-5 max-w-4xl mb-16">
          {econItems.map((t, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="flex items-start gap-3">
                <span className="mt-1 w-2 h-2 rounded-full bg-[#0262B4] shrink-0" />
                <span className="text-[#1E1E24] font-medium leading-snug">{t}</span>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <h3 className="font-display text-2xl font-bold text-[#1E1E24] mb-8">
            What Changes Financially
          </h3>
        </Reveal>
        <ComparisonBlock
          without={["Transactions fail", "Customers leave", "Revenue disappears"]}
          withItems={[
            "Outcomes complete",
            "Customers stay engaged",
            "Previously lost scenarios become monetizable",
          ]}
        />
        <Reveal delay={0.1}>
          <p className="mt-10 text-[#1E1E24] font-medium text-lg max-w-3xl">
            We'll help you model the opportunity against your actual customer base.
          </p>
        </Reveal>
      </Section>

      <CTABanner />
    </>
  );
}
